import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DesignDataCell } from '@/components/result/DesignDataCell';
import { CommentToggleButton, ExpandableCommentTableRows } from './CommentExpand';
import { ScenarioResultBadge } from './ScenarioResultBadge';
import {
  groupPupilPerformanceByPatient,
  PupilEyeValueCell,
  type PupilPatientAggRow,
} from '@/utils/evaluationResultUtils';
import { formatGenderAge, isScenarioAnswerCorrect } from '@/utils/scenarioResultFormat';
import { AssessmentTableShell, type PupilReactionPerformanceRow } from './AssessmentResult';

export function PupilAssessmentTable({
  title,
  performance,
}: {
  title: string;
  performance: PupilReactionPerformanceRow[];
}): React.ReactElement | null {
  const { t } = useTranslation('class');
  const { t: tScenario } = useTranslation('scenario');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  if (!performance?.length) {
    return (
      <AssessmentTableShell title={title}>
        <table className="w-full text-sm text-center" role="presentation">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableNo')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('pupilReactionResult.tableName')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableGenderAge')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableL')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableR')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableResult')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableComment')}</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((r) => (
              <tr className="border-b border-slate-100/80" key={`pupil-skel-${r}`}>
                <td className="px-4 py-3 text-center text-slate-500 font-medium align-top">{r + 1}</td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="이름" align="left" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="성/연" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="좌" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="우" align="center" /></td>
                <td className="px-4 py-3 align-top text-center text-slate-500"><DesignDataCell hint="O/X" align="center" /></td>
                <td className="px-4 py-3 align-top text-slate-400 text-center">···</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AssessmentTableShell>
    );
  }

  const patientRows = groupPupilPerformanceByPatient(performance);

  return (
    <AssessmentTableShell title={title}>
      <table className="w-full text-sm text-center">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableNo')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('pupilReactionResult.tableName')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableGenderAge')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableL')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableR')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableResult')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableComment')}</th>
          </tr>
        </thead>
        <tbody>
          {patientRows.map((patient: PupilPatientAggRow, idx: number) => {
            const isExpanded = expandedRow === idx;
            const toggleComment = () => setExpandedRow((prev) => (prev === idx ? null : idx));
            const genderAge = formatGenderAge(patient.gender, patient.age);
            const resultVal = patient.result;
            const isCorrect = isScenarioAnswerCorrect(resultVal);
            const cellClass = 'px-4 py-3 text-center text-gray-700';
            return (
              <ExpandableCommentTableRows
                key={idx}
                isExpanded={isExpanded}
                toggleComment={toggleComment}
                colSpan={6}
                comment={patient.comment}
              >
                <td className="px-4 py-3 text-center text-gray-900">{patient.no ?? idx + 1}</td>
                <td className="px-4 py-3 text-start text-gray-900">{patient.name ?? '-'}</td>
                <td className="px-4 py-3 text-center text-gray-600">{genderAge}</td>
                <td className={cellClass}><PupilEyeValueCell r={patient.L} /></td>
                <td className={cellClass}><PupilEyeValueCell r={patient.R} /></td>
                <td className="px-4 py-3 text-center">
                  {resultVal ? (
                    <ScenarioResultBadge
                      ok={isCorrect}
                      labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
                      labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-4 py-3 text-center text-gray-600">
                  <CommentToggleButton
                    isExpanded={isExpanded}
                    onToggle={toggleComment}
                    ariaLabel={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
                  />
                </td>
              </ExpandableCommentTableRows>
            );
          })}
        </tbody>
      </table>
    </AssessmentTableShell>
  );
}

interface PupilResultProps {
  title: string;
  resultMetadata: any;
}

/** content_code `pupil_reaction` 전용 표 (상단 요약은 SimulationResults `EvalSummaryStatGrid`) */
export default function PupilResult({ title, resultMetadata }: PupilResultProps): React.ReactElement | null {
  const performance = resultMetadata?.pupilReactionPerformance;
  return (
    <PupilAssessmentTable
      title={title}
      performance={Array.isArray(performance) ? performance : []}
    />
  );
}
